import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { v7 as uuidv7 } from 'uuid';

import { useScreenWidth } from '@/hooks/useScreenWidth';
import { useToast } from '@/shared/components/Toast';
import { refreshDealsTable } from '@/modules/application-tracker/api';
import { RENTER_INVITE_QUERY_PARAM } from '@/constants/Constants';
import { navigateInOrder } from '../../utils/navigation.utils';
import { InviteLeaseDetailsDrawerFormContent } from './invite-lease-details.drawer.form-content';
import { theme } from '@theguarantors/tg-package-material-ui';
import { type Renter } from '@/utils/types/renter.interface';
import { createInviteLeaseDetailsSuccessToastParams } from './components';
import { useInviteDrawerSessionTracking, useInviteLeaseDetailsAction } from './hooks';
import { useNewDealEmail } from './invite-lease-details.host';
import { type InviteSession, InviteSessionProvider } from './invite-session.context';
import type { CloseReason, InviteNavigationState } from './types';

type SessionData = Omit<InviteSession, 'reportFormDirty'>;

const startSession = (navigationState: InviteNavigationState | null): SessionData => ({
  flowId: uuidv7(),
  entryPoint: navigationState?.inviteEntryPoint ?? 'direct_link',
  fromUrl: navigationState?.fromUrl ?? null,
});

const getDrawerProps = (isDesktop: boolean) =>
  isDesktop
    ? {
        headerPadding: { py: 2, px: 4 },
        contentPadding: { py: 3, px: 4 },
        paperWidth: '520px',
        anchor: 'right' as const,
        paperHeight: '100%',
        borderRadius: '0',
      }
    : {
        headerPadding: { pt: '24px', pb: '16px', px: 2 },
        contentPadding: { py: '16px', px: 2 },
        paperWidth: '100%',
        anchor: 'bottom' as const,
        paperHeight: 'min-content',
        borderRadius: '24px 24px 0 0',
      };

export function InviteLeaseDetailsDrawer({ isOpen }: { isOpen: boolean }) {
  const { isDesktop } = useScreenWidth();
  const location = useLocation();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { setNewDealEmail } = useNewDealEmail();
  const { openInviteLeaseDetails } = useInviteLeaseDetailsAction();

  // Each opening is a new session (flow id + entry point) and a fresh form.
  const [opening, setOpening] = useState<{ isOpen: boolean; data: SessionData | null }>({
    isOpen: false,
    data: null,
  });
  if (opening.isOpen !== isOpen) {
    setOpening({
      isOpen,
      data: isOpen ? startSession(location.state as InviteNavigationState | null) : opening.data,
    });
  }

  // The form reports each dirty flip (rare: first edit, back to clean, unmount) for the close guard.
  const [isFormDirty, setIsFormDirty] = useState(false);
  const session = useMemo<InviteSession | null>(
    () => (opening.data ? { ...opening.data, reportFormDirty: setIsFormDirty } : null),
    [opening.data],
  );

  const closeDrawer = () => {
    if ((location.state as InviteNavigationState | null)?.closesWithBack) {
      navigateInOrder(() => navigate(-1));
      return;
    }
    // Opened from a link: there is no entry of ours to go back to.
    setSearchParams(
      (params) => {
        params.delete(RENTER_INVITE_QUERY_PARAM);
        return params;
      },
      { replace: true },
    );
  };

  const queryClient = useQueryClient();
  const toast = useToast();
  const drawerProps = getDrawerProps(isDesktop);

  const { recordInviteFlowMilestone, trackDrawerClosed } = useInviteDrawerSessionTracking(
    isOpen,
    session,
  );

  // Ref so the unmount cleanup always calls the latest trackDrawerClosed without it being a dep.
  const trackDrawerClosedRef = useRef(trackDrawerClosed);
  const hasOpenSessionRef = useRef(false);

  useEffect(() => {
    trackDrawerClosedRef.current = trackDrawerClosed;
  }, [trackDrawerClosed]);

  useEffect(() => {
    hasOpenSessionRef.current = isOpen;
  }, [isOpen]);

  // Fires DrawerClosed on unmount only when the drawer was still open (e.g., route change).
  // closeWithReason() clears hasOpenSessionRef first, so normal closes don't double-fire here.
  useEffect(() => {
    return () => {
      if (hasOpenSessionRef.current) {
        trackDrawerClosedRef.current('unmount');
      }
    };
  }, []);

  const closeWithReason = (reason: CloseReason) => {
    hasOpenSessionRef.current = false;
    trackDrawerClosed(reason);
    closeDrawer();
  };

  const showSuccessToast = (emails: string[]) => {
    toast.success(
      createInviteLeaseDetailsSuccessToastParams({
        emails,
        onInviteAnother: () => openInviteLeaseDetails('invite_another'),
        theme,
      }),
    );
  };

  const handleSuccess = (values: Renter) => {
    const emails = values.applicantEmails?.map((e) => e.value).filter(Boolean) ?? [];
    const primaryEmail = values.applicantEmails?.[0]?.value?.trim();
    if (primaryEmail) {
      setNewDealEmail(primaryEmail);
    }
    recordInviteFlowMilestone('hadSubmitSuccess');
    closeWithReason('submit_success');
    showSuccessToast(emails);
    void refreshDealsTable(queryClient);
    queryClient.invalidateQueries({ queryKey: ['deal-type-counts'] });
    queryClient.invalidateQueries({ queryKey: ['deals-status-counts'] });
  };

  const backdrop = {
    id: 'renter-invite-drawer-backdrop',
    'data-testid': 'renter-invite-drawer-backdrop',
    style: {
      backgroundColor: 'rgba(8, 10, 45, 0.4)',
    },
  };

  return (
    <Drawer
      anchor={drawerProps.anchor}
      open={isOpen}
      onClose={(_event, reason) => {
        if (!isFormDirty) {
          closeWithReason(reason === 'escapeKeyDown' ? 'escape' : 'click_outside');
        }
      }}
      slotProps={{ backdrop }}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          border: 'none',
          borderRadius: drawerProps.borderRadius,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: drawerProps.paperHeight,
          width: drawerProps.paperWidth,
          overflow: 'hidden',
          [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100% - 105px)',
            height: '100%',
          },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]}`,
          ...drawerProps.headerPadding,
        }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'background.default',
            flexShrink: 0,
          }}
          p={0}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: '24px !important',
                lineHeight: '36px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '18px !important',
                  lineHeight: '24px !important',
                },
              }}
            >
              Invite your applicants
            </Typography>
            <IconButton
              color="inherit"
              aria-label="close drawer"
              onClick={() => closeWithReason('explicit_close')}
              sx={{ mr: '-12px' }}
              edge="start"
            >
              <CloseIcon
                sx={{
                  [theme.breakpoints.down('md')]: {
                    fontSize: '20px',
                  },
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
          ...drawerProps.contentPadding,
          pb: 0,
          [theme.breakpoints.down('md')]: {
            overflowX: 'hidden',
          },
        }}
      >
        {session ? (
          <InviteSessionProvider value={session}>
            <InviteLeaseDetailsDrawerFormContent
              key={session.flowId}
              onSuccess={handleSuccess}
              onMilestoneReached={recordInviteFlowMilestone}
            />
          </InviteSessionProvider>
        ) : null}
      </Stack>
    </Drawer>
  );
}
